import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { SafeAny } from '../../types/safe-any';

import {
  CANumberById,
  Customer,
  ExportQuotesBundleResponse,
  ICableAssemblyOrder,
  Quote,
  QuotePerson,
  QuoteStatus,
} from './quotes.state.interface';

@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  private readonly httpClient = inject(HttpClient);
  private apiUrl = '/api/quotes'; // Replace with your actual API endpoint

  getQuotes(payload: SafeAny): Observable<HttpResponse<Quote[]>> {
    // Remove null values from payload
    const cleanPayload: SafeAny = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => value !== null),
    );

    return this.httpClient.get<Quote[]>(`${environment.apiUrl}/quotes`, {
      params: cleanPayload,
      observe: 'response',
    });
  }

  addQuote(quote: ICableAssemblyOrder): Observable<ICableAssemblyOrder> {
    return this.httpClient.post<ICableAssemblyOrder>(this.apiUrl, quote);
  }

  updateQuote(payload: ICableAssemblyOrder, quoteId: string): Observable<ICableAssemblyOrder> {
    return this.httpClient.put<ICableAssemblyOrder>(
      `${environment.apiUrl}/quotes/${quoteId}`,
      payload,
    );
  }

  deleteQuote(id: string): Observable<SafeAny> {
    return this.httpClient.delete<Observable<SafeAny>>(`${environment.apiUrl}/quotes/${id}`);
  }

  getAssignees(payload: SafeAny): Observable<QuotePerson[]> {
    return this.httpClient.get<QuotePerson[]>(`${environment.apiUrl}/assignees`, {
      params: payload,
    });
  }

  getCustomers(): Observable<Customer[]> {
    return this.httpClient.get<Customer[]>(`${environment.apiUrl}/customers`);
  }

  getStatuses(payload: SafeAny): Observable<QuoteStatus[]> {
    return this.httpClient.get<QuoteStatus[]>(`${environment.apiUrl}/drop-down-options`, {
      params: payload,
    });
  }

  getQuotesShippingMethods(): Observable<SafeAny> {
    return this.httpClient.get<SafeAny>(
      `${environment.apiUrl}/drop-down-options?identifier=QUOTES_SHIPPING_METHOD`,
    );
  }

  getCANumbers(payload: { identifier: string }): Observable<SafeAny[]> {
    return this.httpClient.get<SafeAny[]>(`${environment.apiUrl}/ca-numbers`, {
      params: payload,
    });
  }

  getCANumberById(id: string, includeRelations: boolean): Observable<CANumberById> {
    return this.httpClient.get<CANumberById>(`${environment.apiUrl}/ca-numbers/${id}`, {
      params: { includeRelations },
    });
  }

  getItemProcesses(payload: SafeAny): Observable<SafeAny[]> {
    return this.httpClient.get<SafeAny[]>(`${environment.apiUrl}/item-processes`, {
      params: payload,
    });
  }

  getItemProcessById(id: string): Observable<SafeAny> {
    return this.httpClient.get<SafeAny>(`${environment.apiUrl}/item-processes/${id}`);
  }

  createQuote(payload: SafeAny): Observable<SafeAny> {
    return this.httpClient.post<SafeAny>(`${environment.apiUrl}/quotes`, payload);
  }

  getQuoteById(id: number): Observable<SafeAny> {
    return this.httpClient.get<SafeAny>(`${environment.apiUrl}/quotes/${id}`);
  }

  getItemProcessesCategories(): Observable<SafeAny[]> {
    return this.httpClient.get<SafeAny[]>(`${environment.apiUrl}/item-processes/categories`);
  }

  saveNote(quoteId: string, notes: string): Observable<SafeAny> {
    return this.httpClient.post<SafeAny>(`${environment.apiUrl}/quotes/${quoteId}/notes`, {
      notes,
    });
  }

  exportQuotesBundle(quotes: string): Observable<ExportQuotesBundleResponse> {
    return this.httpClient.post<ExportQuotesBundleResponse>(`${environment.apiUrl}/quotes/export`, {
      quotes,
    });
  }

  archiveQuote(id: string, archived: boolean): Observable<SafeAny> {
    return this.httpClient.patch<SafeAny>(`${environment.apiUrl}/quotes/${id}`, { archived });
  }

  unarchiveQuote(id: string, archived: boolean): Observable<SafeAny> {
    return this.httpClient.patch<SafeAny>(`${environment.apiUrl}/quotes/${id}`, { archived });
  }

  duplicateQuote(quoteId: string): Observable<{ id: number }> {
    return this.httpClient.post<{ id: number }>(
      `${environment.apiUrl}/quotes/${quoteId}/duplicate`,
      {},
    );
  }
}
